/**
 * SuiteHub — shared layout used by every "Suite" landing page
 * (Teaching Suite, YouTube Suite, Content Suite, AI Studio).
 * 
 * Pass props: title, subtitle, eyebrow, sections [{title, tools[{name, desc, Icon, to, status?}]}]
 */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import PremiumNav from "./PremiumNav";
import PremiumFooter from "./PremiumFooter";
import { PremiumCard, Pill, SectionHeading } from "./Primitives";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, delay },
});

export default function SuiteHub({ eyebrow, title, subtitle, sections, testId = "suite-hub" }) {
  return (
    <div className="min-h-screen bg-white font-sans" data-testid={testId}>
      <PremiumNav />

      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-12">
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] -z-0"
             style={{ background: "radial-gradient(circle, rgba(168,85,247,0.08) 0%, rgba(168,85,247,0) 60%)" }}/>
        <motion.div className="relative" {...fadeUp()}>
          <SectionHeading eyebrow={eyebrow} title={title} subtitle={subtitle} />
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        {sections.map((sec, sIdx) => (
          <div key={sec.title} className={sIdx > 0 ? "mt-16" : ""}>
            <motion.h3 {...fadeUp(0.05)} className="font-heading text-xl font-semibold text-[#111111] mb-5">
              {sec.title}
            </motion.h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sec.tools.map((tool, idx) => {
                const isComing = tool.status === "coming-soon";
                const Wrapper = isComing ? "div" : Link;
                return (
                  <motion.div key={tool.name} {...fadeUp(0.04 * idx)}>
                    <Wrapper
                      {...(isComing ? {} : { to: tool.to || "#" })}
                      data-testid={`suite-tool-${tool.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <PremiumCard
                        interactive={!isComing}
                        hover={!isComing}
                        className={`p-5 h-full ${isComing ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            isComing
                              ? "bg-slate-100 text-slate-400"
                              : "bg-[#111111] text-white group-hover:bg-[#A855F7]"
                          }`}>
                            {tool.Icon ? <tool.Icon className="w-4 h-4"/> : <Lock className="w-4 h-4"/>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-heading text-base font-semibold text-[#111111]">{tool.name}</h4>
                              {tool.badge && <Pill variant="brand">{tool.badge}</Pill>}
                              {isComing && <Pill variant="neutral">Coming soon</Pill>}
                            </div>
                            <p className="mt-1 text-sm text-slate-500 leading-relaxed">{tool.desc}</p>
                            {!isComing && (
                              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#A855F7]">
                                Open <ArrowRight className="w-3 h-3"/>
                              </div>
                            )}
                          </div>
                        </div>
                      </PremiumCard>
                    </Wrapper>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </section>
      <PremiumFooter />
    </div>
  );
}
